import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { setAuthToken as setAPIToken, setMediaAccessToken } from '../utils/api';

const AuthContext = createContext();

// Restore auth from storage synchronously so first render is correct (avoids redirect on refresh)
const getStoredAuth = () => {
  try {
    const token = sessionStorage.getItem('authToken');
    const userData = sessionStorage.getItem('userData');
    if (token && userData) {
      const user = JSON.parse(userData);
      return { token, user };
    }
  } catch (e) {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('mediaAccessToken');
  }
  return { token: null, user: null };
};

const stored = getStoredAuth();

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthTokenState] = useState(stored.token);
  const [user, setUser] = useState(stored.user);
  const [isAuthenticated, setIsAuthenticated] = useState(!!stored.token);

  // Sync API token when we have restored token (e.g. on refresh)
  useEffect(() => {
    if (stored.token) {
      setAPIToken(stored.token);
      setMediaAccessToken(sessionStorage.getItem('mediaAccessToken'));
    }
  }, []);

  // When backend returns 401 (e.g. Invalid token), clear auth and redirect to login
  useEffect(() => {
    const onSessionExpired = () => {
      setAuthTokenState(null);
      setAPIToken(null);
      setMediaAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth-session-expired', onSessionExpired);
    return () => window.removeEventListener('auth-session-expired', onSessionExpired);
  }, []);

  const login = useCallback((token, userData, mediaAccessToken = null) => {
    setAuthTokenState(token);
    setAPIToken(token); // Sync with api.js
    setMediaAccessToken(mediaAccessToken);
    setUser(userData);
    setIsAuthenticated(true);
    // Store in sessionStorage for page refresh persistence
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('userData', JSON.stringify(userData));
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend to blacklist token
      const { authAPI } = await import('../utils/api');
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear frontend state regardless of API call result
      setAuthTokenState(null);
      setAPIToken(null); // Clear from api.js
      setMediaAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('mediaAccessToken');
    }
  }, []);

  const getUserId = useCallback(() => {
    return user?.id || null;
  }, [user?.id]);

  const getUserEmail = useCallback(() => {
    return user?.email || null;
  }, [user?.email]);

  const getUserRole = useCallback(() => {
    return user?.role || null;
  }, [user?.role]);

  /** Merge partial fields into stored user (e.g. after profile save). Persists to sessionStorage. */
  const updateUser = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      try {
        sessionStorage.setItem('userData', JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      authToken,
      user,
      isAuthenticated,
      login,
      logout,
      getUserId,
      getUserEmail,
      getUserRole,
      updateUser,
    }),
    [authToken, user, isAuthenticated, login, logout, getUserId, getUserEmail, getUserRole, updateUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

